import { useState, useEffect } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartPyramid } from '@fortawesome/pro-regular-svg-icons';
import { FormLabel, FormSelect, SelectOption, StyledFlex } from '@tryhackme/thm-ui-components';
import { SkillMatrixGraphic, type SkillsMatrixScore } from 'src/common/components';
import { useIsDarkMode } from 'src/common/hooks/use-is-dark-mode';
import { useFeatureIsOn } from 'src/common/growthbook';
import { RoleProps } from '../../skills-matrix.types';
import { SkillDetails } from '../skill-details/skill-details';
import { useGetRoomsBySkillQuery } from '../../skills-matrix.slice';
import { StyledContentWrapper, StyledHeader, StyledSegmentWrapper, StyledTitleWrapper, StyledSelectIcon } from './content.styles';
import { SEGMENTS_OPTIONS } from './content.constants';
import { SegmentSelectOption } from './content.types';
import { ShareSkillsModal } from './share-skills-modal/share-skills-modal';
export const Content = ({
  role
}: {
  role: RoleProps;
}) => {
  const [isSkillDetailsOpen, setIsSkillDetailsOpen] = useState(false);
  const [skillSelected, setSkillSelected] = useState<SkillsMatrixScore | undefined>();
  const [selectedSegmentOptions, setSelectedSegmentOptions] = useState<SegmentSelectOption>((SEGMENTS_OPTIONS[0] as SegmentSelectOption));
  const isDarkMode = useIsDarkMode();
  const isSegmentedMatrixEnabled = useFeatureIsOn('segmented-matrix');
  useEffect(() => {
    const savedSegment = localStorage.getItem('selectedSegmentSkills');
    if (savedSegment) {
      const segmentOption = SEGMENTS_OPTIONS.find(option => option.value === savedSegment);
      if (segmentOption) {
        setSelectedSegmentOptions((segmentOption as SegmentSelectOption));
      }
    }
  }, []);
  const handleSegmentChange = (option: SelectOption) => {
    setSelectedSegmentOptions((option as SegmentSelectOption));
    localStorage.setItem('selectedSegmentSkills', option.value);
  };
  const {
    data: roomsData,
    isFetching
  } = useGetRoomsBySkillQuery(skillSelected ? {
    skill: skillSelected,
    role: role?.label,
    segment: isSegmentedMatrixEnabled ? selectedSegmentOptions.value : 'all'
  } : skipToken);
  const segmentOptions = SEGMENTS_OPTIONS.map(option => ({
    ...option,
    icon: <StyledSelectIcon icon={option.icon} $segment={option.value} $isSelected={selectedSegmentOptions?.value === option.value} $isDarkMode={isDarkMode} />
  }));
  return <StyledContentWrapper data-sentry-element="StyledContentWrapper" data-sentry-component="Content" data-sentry-source-file="content.tsx">
      <StyledHeader data-sentry-element="StyledHeader" data-sentry-source-file="content.tsx">
        <StyledTitleWrapper data-skill={role?.id} data-sentry-element="StyledTitleWrapper" data-sentry-source-file="content.tsx">
          <h2>
            <FontAwesomeIcon icon={role?.icon ?? faChartPyramid} data-sentry-element="FontAwesomeIcon" data-sentry-source-file="content.tsx" />{' '}
            {isSegmentedMatrixEnabled && selectedSegmentOptions?.label} {role?.label}
          </h2>
          <p>Select a skill to see which rooms to complete to upskill</p>
        </StyledTitleWrapper>
        {isSegmentedMatrixEnabled && <StyledFlex gap="1.6rem" flexWrap="no-wrap" alignItems="flex-start">
            <StyledSegmentWrapper $isDarkMode={isDarkMode} $segment={selectedSegmentOptions.value}>
              <FormLabel variant="text" labelPosition="left">
                Select level
              </FormLabel>

              <FormSelect isMulti={false} isSearchable={false} options={segmentOptions} selected={selectedSegmentOptions} handleSelection={handleSegmentChange} className="segment-select" />
            </StyledSegmentWrapper>
            <ShareSkillsModal role={role?.label} segment={isSegmentedMatrixEnabled ? selectedSegmentOptions.value : 'all'} />
          </StyledFlex>}
      </StyledHeader>

      <SkillMatrixGraphic role={role?.label} segment={isSegmentedMatrixEnabled ? selectedSegmentOptions.value : 'all'} roleId={role?.id} isClickable onSkillSelect={skill => {
      setSkillSelected(skill);
      setIsSkillDetailsOpen(true);
    }} data-sentry-element="SkillMatrixGraphic" data-sentry-source-file="content.tsx" />

      <SkillDetails rooms={roomsData?.data?.docs} isSkillDetailsOpen={isSkillDetailsOpen} setIsSkillDetailsOpen={setIsSkillDetailsOpen} skillSelected={skillSelected} isFetching={isFetching} setSkillSelected={setSkillSelected} data-sentry-element="SkillDetails" data-sentry-source-file="content.tsx" />
    </StyledContentWrapper>;
};